import { useEffect, useState } from "react";
import axios from "axios";
import { Col, Row, Spinner } from "react-bootstrap";
import { formatDate } from "../epicLeaderboard/format";
import type { PlayerGquestStats as Stats } from "../../types/playerGquests";
import "../epicLeaderboard/epicLeaderboard.css";
import "./pk/pk.css";

type Props = { playerName: string; startDate?: string };

const rangeLabel = (min: number, max: number) => min === max ? String(min) : `${min}–${max}`;
const resultLabel: Record<string, string> = {
  WON: "Won",
  EXT_COMPLETED: "Extended completion",
  DNF: "Did not finish"
};

const Tile = ({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone: string }) => (
  <div className={`epic-lb-card player-gquest-tile player-gquest-tile--${tone}`}>
    <div className="epic-lb-card-title">{label}</div>
    <div className="epic-lb-tile-value">{value}</div>
    {sub && <div className="epic-lb-muted">{sub}</div>}
  </div>
);

const PlayerGquestStats = ({ playerName, startDate }: Props) => {
  const [data, setData] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    axios.get<Stats>(`${process.env.REACT_APP_API}public/gquests/player`, {
      params: { player: playerName, ...(startDate ? { startDate } : {}) },
      signal: controller.signal
    }).then(response => {
      if (!controller.signal.aborted) setData(response.data);
    }).catch(requestError => {
      if (axios.isCancel(requestError) || controller.signal.aborted) return;
      console.error("Failed to fetch player global quest stats", requestError);
      setError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [playerName, startDate]);

  if (loading && !data) return <div className="text-center my-5"><Spinner animation="border" variant="light" /></div>;
  if (error) return <div className="epic-lb-card text-center mt-4">Couldn&apos;t load GQ stats.</div>;
  if (!data || data.summary.entries === 0) return <div className="epic-lb-card text-center mt-4">No global quest results for this player in this range.</div>;

  const { summary } = data;
  return <div className={loading ? "epic-lb-refetching" : undefined}>
    <Row className="g-3 mt-3 mb-4">
      <Col xs={6} lg><Tile label="GQ wins" value={summary.wins} tone="wins" /></Col>
      <Col xs={6} lg><Tile label="GQs entered" value={summary.entries} tone="entries" /></Col>
      <Col xs={6} lg><Tile label="Win rate" value={`${summary.winRate}%`} tone="rate" /></Col>
      <Col xs={6} lg><Tile label="Median win" value={summary.medianWinningDuration ?? "—"} tone="time" /></Col>
      <Col xs={6} lg><Tile label="Extended completions" value={summary.extendedCompletions} sub={`${summary.dnfs} did not finish`} tone="extended" /></Col>
    </Row>

    <Row className="g-3 mb-4">
      <Col md={6}><WinRecord label="Fastest win" record={summary.fastestWin} tone="fastest" /></Col>
      <Col md={6}><WinRecord label="Longest win" record={summary.longestWin} tone="longest" /></Col>
    </Row>

    <section className="mb-4">
      <div className="epic-lb-card-title">Range breakdown</div>
      <div className="epic-lb-table-scroll"><table className="epic-lb-table"><thead><tr><th>Range</th><th>Entries</th><th>Wins</th><th>Win rate</th></tr></thead><tbody>{data.ranges.map(range => <tr key={`${range.levelMin}-${range.levelMax}`}><td>{rangeLabel(range.levelMin, range.levelMax)}</td><td>{range.entries}</td><td>{range.wins}</td><td>{range.winRate}%</td></tr>)}</tbody></table></div>
    </section>

    <section className="mb-5">
      <div className="epic-lb-card-title">Recent GQs</div>
      <div className="epic-lb-table-scroll"><table className="epic-lb-table"><thead><tr><th>Completed</th><th>GQ #</th><th>Range</th><th>Duration</th><th>Result</th><th>Mobs left</th></tr></thead><tbody>{data.history.map((row, index) => <tr key={`${row.gqNumber}-${row.endedAt}-${index}`}><td>{formatDate(row.endedAt)}</td><td>{row.gqNumber}</td><td>{rangeLabel(row.levelMin, row.levelMax)}</td><td>{row.duration}</td><td><span className={`player-gquest-result player-gquest-result--${row.result.toLowerCase()}`}>{resultLabel[row.result]}</span></td><td>{row.mobsLeftFinal}</td></tr>)}</tbody></table></div>
    </section>
  </div>;
};

const WinRecord = ({ label, record, tone }: { label: string; record: Stats["summary"]["fastestWin"]; tone: string }) => (
  <div className={`epic-lb-card player-gquest-record player-gquest-record--${tone}`}>
    <div className="epic-lb-card-title">{label}</div>
    {record ? <><div className="epic-lb-tile-value">{record.duration}</div><div className="epic-lb-muted">GQ #{record.gqNumber} · {rangeLabel(record.levelMin, record.levelMax)} · {formatDate(record.endedAt)}</div></> : <div className="epic-lb-muted">No wins in this range.</div>}
  </div>
);

export default PlayerGquestStats;
